import { sampleProfile } from './profileGeometry.js';
import { frameSpec, thresholdSpec } from './doorFrameGeometry.js';
import { SIDE_PROFILE_MM, BOTTOM_PROFILE_MM } from './doorLeafGeometry.js';
import { resolveWaternose } from './waternoseGeometry.js';

export const ASSEMBLY_GAP_MM = 4;

const edges = (path, closed) => path.slice(0, closed ? path.length : -1)
  .map((a, i) => [a, path[(i + 1) % path.length]]);

// Both contours are [Z, distance]. On each shared depth interval the
// separation is linear, so its limiting value is at an interval endpoint.
function requiredOffset(obstacles, leafEdges) {
  let offset = -Infinity;
  for (const [a,b] of obstacles) for (const [c,d] of leafEdges) {
    const lo = Math.max(Math.min(a[0],b[0]), Math.min(c[0],d[0]));
    const hi = Math.min(Math.max(a[0],b[0]), Math.max(c[0],d[0]));
    if (lo > hi) continue;
    const value = (p,q,z,upper) => Math.abs(q[0]-p[0]) < 1e-9
      ? (upper ? Math.max(p[1],q[1]) : Math.min(p[1],q[1]))
      : p[1]+(q[1]-p[1])*(z-p[0])/(q[0]-p[0]);
    for (const z of [lo,hi]) offset = Math.max(offset, value(a,b,z,true)-value(c,d,z,false));
  }
  if (!Number.isFinite(offset)) throw new Error('Frame/threshold and leaf profiles must overlap in depth.');
  return offset + ASSEMBLY_GAP_MM;
}

export function assemblyClearances(design) {
  const frame = edges(sampleProfile(frameSpec(design.frame).profileMm, { closed:true }).map(([x,z]) => [z,x]), true);
  const sill = thresholdSpec(design.threshold, design.thresholds).profiles
    .flatMap(p => edges(sampleProfile(p, { closed:true }), true));
  const leaf = p => edges(sampleProfile(p).map(([x,z]) => [z,x]), false);
  let side = leaf(design.sideProfileMm || SIDE_PROFILE_MM);
  let top = leaf(design.topProfileMm || design.sideProfileMm || SIDE_PROFILE_MM);
  let bottom = leaf(design.bottomProfileMm || BOTTOM_PROFILE_MM);
  if (design.waternose?.enabled) {
    const nose = resolveWaternose(design.waternose, design.heightMm || 2000);
    const ys = nose.profileMm.map(p => p[1]);
    const zs = nose.profileMm.map(p => p[0]);
    side = [...side, [[Math.min(...zs),0],[Math.max(...zs),0]]];
    if (nose.anchor === 'top') top = edges(nose.profileMm.map(([z,y]) => [z,Math.max(...ys)-y]), true);
    else bottom = [...bottom, ...edges(nose.profileMm.map(([z,y]) => [z,y-Math.min(...ys)]), true)];
  }
  return { sideMm:requiredOffset(frame,side), topMm:requiredOffset(frame,top), bottomMm:requiredOffset(sill,bottom) };
}
